import { inject, Injectable, InjectionToken } from '@angular/core';

/**
 * Injection token for the browser's localStorage.
 * Using a token (instead of the global) makes services testable without
 * touching the shared happy-dom / jsdom storage.
 */
export const LOCAL_STORAGE = new InjectionToken<Storage>('LOCAL_STORAGE', {
  providedIn: 'root',
  factory: () => window.localStorage,
});

/**
 * Service for managing user-scoped localStorage values
 *
 * Problem: When multiple users share a device (common during testing),
 * localStorage values from one user can leak to another user's session.
 *
 * Solution: Prefix all user-specific localStorage keys with the user's email.
 *
 * Usage:
 *   // User-scoped storage (most common):
 *   this.userStorage.getItem('user_language')
 *   this.userStorage.setItem('user_language', 'en')
 *
 *   // Global device settings:
 *   this.userStorage.getGlobalItem('crsql-site-id')
 *   this.userStorage.setGlobalItem('crsql-site-id', id)
 *
 *   // User-scoped with global fallback (e.g., language before auth):
 *   this.userStorage.getItemWithGlobalFallback('user_language')
 *
 *   // Save to both user and global (e.g., language):
 *   this.userStorage.setItemBoth('user_language', 'en')
 *
 * Keys that should be global (device settings):
 *   - PWA install prompt timestamp
 *   - Eruda debug window.console enabled
 *   - crsql-site-id (database sync identity)
 *   - data-delay-* (testing/debug settings)
 *   - translation_debug (developer tool)
 *   - last_test_email (convenience for testing)
 */
@Injectable({ providedIn: 'root' })
export class UserStorageService {
  private storage = inject(LOCAL_STORAGE);
  private readonly TOKEN_KEY = 'auth_token';

  /**
   * Decode JWT token payload without verification
   * Returns null if token is invalid or missing
   */
  private decodeToken(token: string): any | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  /**
   * Get the current user's email for prefixing
   * Falls back to 'anonymous' if not authenticated
   */
  private getUserPrefix(): string {
    const token = this.storage.getItem(this.TOKEN_KEY);
    if (!token) return 'anonymous';

    const payload = this.decodeToken(token);
    return payload?.email || 'anonymous';
  }

  /**
   * Get the current user's email (public accessor).
   * Reads from localStorage directly since the auth token is stored there.
   * Falls back to 'anonymous' if not authenticated.
   */
  getCurrentUserEmail(): string {
    try {
      const token = this.storage.getItem(this.TOKEN_KEY);
      if (!token) return 'anonymous';
      const payload = this.decodeToken(token);
      return payload?.email || 'anonymous';
    } catch {
      return 'anonymous';
    }
  }

  /**
   * Build user-scoped key
   */
  private scopedKey(key: string): string {
    return `user:${this.getUserPrefix()}:${key}`;
  }

  /**
   * Get a user-scoped item from localStorage
   * Returns null if not found (empty string is a valid value)
   */
  getItem(key: string): string | null {
    return this.storage.getItem(this.scopedKey(key));
  }

  /**
   * Set a user-scoped item in localStorage
   */
  setItem(key: string, value: string): void {
    this.storage.setItem(this.scopedKey(key), value);
  }

  /**
   * Remove a user-scoped item from localStorage
   */
  removeItem(key: string): void {
    this.storage.removeItem(this.scopedKey(key));
  }

  /**
   * Get a global (non-user-scoped) item from localStorage
   * Use this for device-level settings that should persist across all users
   */
  getGlobalItem(key: string): string | null {
    return this.storage.getItem(key);
  }

  /**
   * Set a global (non-user-scoped) item in localStorage
   * Use this for device-level settings that should persist across all users
   */
  setGlobalItem(key: string, value: string): void {
    this.storage.setItem(key, value);
  }

  /**
   * Remove a global item from localStorage
   */
  removeGlobalItem(key: string): void {
    this.storage.removeItem(key);
  }

  /**
   * Get item with global fallback
   * Tries user-scoped first, then falls back to global
   * Note: Returns null ONLY if neither exists (empty string is valid)
   */
  getItemWithGlobalFallback(key: string): string | null {
    const userScoped = this.getItem(key);
    if (userScoped !== null) {
      return userScoped;
    }
    return this.getGlobalItem(key);
  }

  /**
   * Set item in both user-scoped and global storage
   * Useful for settings that need a global default but user-specific override
   * (e.g., language preference)
   */
  setItemBoth(key: string, value: string): void {
    this.setItem(key, value);
    this.setGlobalItem(key, value);
  }

  /**
   * Clear all user-scoped items for the current user
   * Called on logout to prevent data leakage
   */
  clearUserData(): void {
    const prefix = `user:${this.getUserPrefix()}:`;
    const keysToRemove = Object.keys(this.storage).filter((key) => key.startsWith(prefix));
    keysToRemove.forEach((key) => this.storage.removeItem(key));
  }

  /**
   * Get all keys for the current user (for debugging)
   */
  getAllUserKeys(): string[] {
    const prefix = `user:${this.getUserPrefix()}:`;
    return Object.keys(this.storage)
      .filter((key) => key.startsWith(prefix))
      .map((key) => key.substring(prefix.length));
  }

  /**
   * Get all global (unprefixed) keys from localStorage
   * Used for debugging and iteration over global settings
   */
  getAllGlobalKeys(): string[] {
    return Object.keys(this.storage);
  }

  // ========== JSON-based typed storage ==========
  // These methods use JSON.stringify/parse to avoid truthy string bugs
  // (e.g., the string 'false' is truthy, but JSON.parse('false') is boolean false)

  /**
   * Get a typed value from user-scoped localStorage
   * Uses JSON.parse for proper type coercion
   * Returns null if not found or if JSON parsing fails
   */
  getTypedItem<T>(key: string): T | null {
    const value = this.getItem(key);
    if (value === null) return null;
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }

  /**
   * Set a typed value in user-scoped localStorage
   * Uses JSON.stringify for consistent serialization
   */
  setTypedItem<T>(key: string, value: T): void {
    this.setItem(key, JSON.stringify(value));
  }

  /**
   * Get a typed value from global localStorage
   * Uses JSON.parse for proper type coercion
   * Returns null if not found or if JSON parsing fails
   */
  getGlobalTypedItem<T>(key: string): T | null {
    const value = this.getGlobalItem(key);
    if (value === null) return null;
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }

  /**
   * Set a typed value in global localStorage
   * Uses JSON.stringify for consistent serialization
   */
  setGlobalTypedItem<T>(key: string, value: T): void {
    this.setGlobalItem(key, JSON.stringify(value));
  }
}
